import { Controller } from '@hotwired/stimulus'

interface AttributeType {
  path: string
  type: string
}

interface ParameterType {
  name: string
  type: 'string' | 'integer' | 'boolean'
  required: boolean
  pattern?: string
  description?: string
  in?: string
  default?: any
}

interface EndpointType {
  response_example?: any
  nodes_by_resource_type: {
    collection: string[]
    single: string[]
  }
  attributes_by_node: Record<string, AttributeType[]>
  parameters?: ParameterType[]
}

export default class extends Controller<any> {
  static targets = [
    'endpoint',
    'kind',
    'node',
    'attributeSelect',
    'responseExample',
    'parametersCard',
    'parametersDescription',
    'parametersFields',
    'parametersEmptyState',
    'parameterTemplate',
    'inputTemplate',
    'valueInput',
    'condition',
    'conditionValueType'
  ]

  static values = {
    existingParameters: Array
  }

  declare endpointTarget: HTMLSelectElement
  declare kindTargets: HTMLInputElement[]
  declare nodeTarget: HTMLSelectElement
  declare attributeSelectTargets: HTMLSelectElement[]
  declare valueInputTargets: HTMLInputElement[]
  declare conditionTargets: HTMLElement[]
  declare conditionValueTypeTargets: HTMLInputElement[]
  declare responseExampleTarget: HTMLElement
  declare hasResponseExampleTarget: boolean
  declare parametersCardTarget: HTMLElement
  declare hasParametersCardTarget: boolean
  declare parametersDescriptionTarget: HTMLElement
  declare parametersFieldsTarget: HTMLElement
  declare parametersEmptyStateTarget: HTMLElement
  declare parameterTemplateTarget: HTMLTemplateElement
  declare inputTemplateTarget: HTMLTemplateElement
  declare existingParametersValue: Array<{ name: string, value: string, id?: string, errors?: string[] }>

  private readonly ROOT_NODE = '$'
  private readonly ROOT_NODE_PREFIX = '$.'
  private readonly DESTROY_MARKER_CLASS = 'parameter-destroy-marker'
  private readonly PARAMETER_FIELD_CLASS = 'parameter-field'
  private readonly PARAMETER_VALUE_INPUT_CLASS = 'parameter-value-input'

  private endpointsData: Record<string, EndpointType> = {}
  private currentEndpoint: EndpointType | null = null
  private requestIndex: number = 0
  private existingParametersFromServer: Record<string, { value: string, id?: string, errors?: string[] }> = {}

  connect (): void {
    this.requestIndex = this.extractRequestIndex()
    this.loadEndpointsMetadata()
    this.loadExistingParametersFromDOM()
    this.hideParametersCardIfNoEndpoint()
    this.restoreEndpointState()
  }

  endpointChanged (): void {
    this.removePlaceholderOption()
    this.removeNonPersistedConditions()
    this.clearAllValues()
    this.refresh()
  }

  kindChanged (): void {
    this.clearAllValues()
    this.updateNodes()
  }

  nodeChanged (): void {
    this.clearAllValues()
    this.updateAttributeSelects()
  }

  attributeChanged (event: Event): void {
    const select = event.target as HTMLSelectElement
    if (select == null) return

    this.updateValueTypeForField(select)
  }

  conditionAdded (): void {
    setTimeout(() => {
      const newCondition = this.attributeSelectTargets[this.attributeSelectTargets.length - 1]
      if (newCondition == null) return

      this.updateValueTypeForField(newCondition)
      this.updateAttributeOptions(newCondition)
    })
  }

  private clearAllValues (): void {
    this.valueInputTargets.forEach(input => {
      input.value = ''
    })

    this.conditionValueTypeTargets.forEach(input => {
      input.value = ''
    })
  }

  private removeNonPersistedConditions (): void {
    this.conditionTargets.forEach(condition => {
      condition.dispatchEvent(new CustomEvent('removable:remove', { bubbles: true }))
    })
  }

  private hideParametersCardIfNoEndpoint (): void {
    if (!this.hasParametersCardTarget || this.endpointTarget.value !== '') return
    this.parametersCardTarget.style.display = 'none'
  }

  private restoreEndpointState (): void {
    if (this.endpointTarget.value === '') return
    this.removePlaceholderOption()
    this.refresh(true)
  }

  private removePlaceholderOption (): void {
    if (this.endpointTarget.options[0]?.value === '' && this.endpointTarget.value !== '') {
      this.endpointTarget.options.remove(0)
    }
  }

  private refresh (preserveValues: boolean = false): void {
    this.currentEndpoint = this.endpointsData[this.endpointTarget.value] ?? null
    if (this.currentEndpoint == null) return

    this.autoselectKind()
    this.updateParameters(preserveValues)
    this.updateNodes()
    this.updateResponseExample()
  }

  private toggleInputField (inputs: HTMLInputElement[], typeToEnable: string | null): void {
    inputs.forEach(input => {
      const inputType = input.dataset.type
      if (inputType === typeToEnable) {
        input.classList.remove('hidden')
        input.disabled = false
      } else {
        input.classList.add('hidden')
        input.disabled = true
        input.value = ''
      }
    })
  }

  private autoselectKind (): void {
    if (this.selectedKind() === '') {
      this.kindTargets[0].checked = true
    }
  }

  private selectedKind (): string {
    return this.kindTargets.find(r => r.checked)?.value ?? ''
  }

  private updateNodes (): void {
    if (this.currentEndpoint == null) return

    const select = this.nodeTarget
    const initial = select.dataset.initialNodeValue
    const kind = this.selectedKind()
    const nodes = kind === 'collection'
      ? this.currentEndpoint.nodes_by_resource_type.collection
      : this.currentEndpoint.nodes_by_resource_type.single

    select.replaceChildren()
    nodes.forEach(node => select.add(new Option(this.displayNode(node), node)))

    const hasInitial = typeof initial === 'string' && initial.length > 0 && nodes.includes(initial)
    select.value = hasInitial ? initial : (nodes[0] ?? '')

    delete select.dataset.initialNodeValue

    this.updateAttributeSelects()
  }

  private updateAttributeSelects (): void {
    this.attributeSelectTargets.forEach(select => {
      this.updateAttributeOptions(select)
    })
  }

  private updateAttributeOptions (select: HTMLSelectElement): void {
    const node = this.nodeTarget.value
    const attributesByNode = this.currentEndpoint?.attributes_by_node ?? {}
    const attributes = attributesByNode[node] ?? []
    const initial = select.dataset.initialAttributeValue

    select.replaceChildren()

    for (const attr of attributes) {
      const label = this.displayAttribute(node, attr.path)
      const option = new Option(label, attr.path)
      option.dataset.type = attr.type
      select.add(option)
    }

    const hasInitial =
      typeof initial === 'string' &&
      initial.length > 0 &&
      attributes.some(a => a.path === initial)

    select.value = hasInitial ? (initial) : (attributes[0]?.path ?? '')
    this.updateValueTypeForField(select)
  }

  private displayNode (node: string): string {
    if (node === this.ROOT_NODE) return 'Root'
    if (node.startsWith(this.ROOT_NODE_PREFIX)) return node.slice(this.ROOT_NODE_PREFIX.length)
    return node
  }

  private updateValueTypeForField (select: HTMLSelectElement): void {
    const option = select.selectedOptions[0]
    const fieldType = option?.dataset.type
    if (fieldType == null) return

    const container = this.conditionTargets.find(c => c.contains(select))
    if (container == null) return

    const typeInput = this.conditionValueTypeTargets.find(input => container.contains(input))
    if (typeInput != null) {
      typeInput.value = fieldType
    }

    const inputs = this.valueInputTargets.filter(input => container.contains(input))

    this.toggleInputField(inputs, fieldType === 'integer' ? 'number' : fieldType)
  }

  private updateResponseExample (): void {
    if (!this.hasResponseExampleTarget || (this.currentEndpoint == null)) return

    const example = this.currentEndpoint.response_example
    this.toggleVisibility(this.responseExampleTarget, example != null)
    if (example != null) {
      this.responseExampleTarget.classList.remove('hidden')
      this.responseExampleTarget.textContent = JSON.stringify(example, null, 2)
    } else {
      this.responseExampleTarget.classList.add('hidden')
      this.responseExampleTarget.textContent = ''
    }
  }

  private displayAttribute (node: string, fullPath: string): string {
    if (node === '$') {
      return fullPath.replace(/^\$\./, '')
    }

    const prefix = `${node}.`
    return fullPath.startsWith(prefix)
      ? fullPath.slice(prefix.length)
      : fullPath
  }

  private loadEndpointsMetadata (): void {
    Array.from(this.endpointTarget.options).forEach(option => {
      if (option.value === '') return

      const { nodesByResourceType, responseExample, attributesByNode, parameters } = option.dataset
      if ((nodesByResourceType == null) || (nodesByResourceType === '')) {
        console.warn('No nodesByResourceType found for option', option.value)
        return
      }

      try {
        this.endpointsData[option.value] = {
          nodes_by_resource_type: JSON.parse(nodesByResourceType),
          attributes_by_node: attributesByNode != null ? JSON.parse(attributesByNode) : {},
          response_example: ((responseExample != null) && (responseExample !== '')) ? JSON.parse(responseExample) : undefined,
          parameters: ((parameters != null) && (parameters !== '')) ? JSON.parse(parameters) : []
        }
      } catch (e) {
        console.error('Failed to parse endpoint metadata for option', option.value, e)
      }
    })
  }

  private updateParameters (preserveValues: boolean = false): void {
    if (this.currentEndpoint == null) return

    const parameters = this.currentEndpoint.parameters ?? []
    const currentValues = preserveValues ? this.getExistingParameterValues() : {}

    if (!preserveValues) {
      this.addDestroyMarkersForExistingParameters()
    }

    this.clearParameterFields()

    if (parameters.length === 0) {
      this.toggleVisibility(this.parametersCardTarget, false)
      return
    }

    this.showParametersUI()
    this.renderParameterFields(parameters, currentValues, preserveValues)
    this.existingParametersFromServer = {}
  }

  private showParametersUI (): void {
    this.toggleVisibility(this.parametersCardTarget, true)
    this.toggleVisibility(this.parametersDescriptionTarget, true)
    this.toggleVisibility(this.parametersEmptyStateTarget, false)
    this.toggleVisibility(this.parametersFieldsTarget, true)
  }

  private renderParameterFields (parameters: ParameterType[], currentValues: Record<string, { value: string, id?: string, errors?: string[] }>, preserveValues: boolean): void {
    parameters.forEach((param, index) => {
      const existing = currentValues[param.name] ?? (preserveValues ? this.existingParametersFromServer[param.name] : undefined)
      const field = this.createParameterField(param, index, existing?.value, existing?.id, existing?.errors)
      this.parametersFieldsTarget.appendChild(field)
    })
  }

  private clearParameterFields (): void {
    const destroyMarkers = this.parametersFieldsTarget.querySelectorAll(`.${this.DESTROY_MARKER_CLASS}`)
    const fields = this.parametersFieldsTarget.querySelectorAll(`.${this.PARAMETER_FIELD_CLASS}`)
    fields.forEach(field => field.remove())

    destroyMarkers.forEach(marker => this.parametersFieldsTarget.appendChild(marker))
  }

  private addDestroyMarkersForExistingParameters (): void {
    const existingParams = this.getExistingParameterValues()
    Object.entries(existingParams).forEach(([, paramData], index) => {
      if ((paramData.id == null) || (paramData.id === '')) return

      const marker = this.createDestroyMarker(index, paramData.id)
      this.parametersFieldsTarget.appendChild(marker)
    })
  }

  private createDestroyMarker (index: number, id: string): HTMLElement {
    const namePrefix = this.buildParameterNamePrefix(index)
    const container = document.createElement('div')
    container.style.display = 'none'
    container.classList.add(this.DESTROY_MARKER_CLASS)
    container.innerHTML = `
      <input type="hidden" name="${namePrefix}[id]" value="${id}">
      <input type="hidden" name="${namePrefix}[_destroy]" value="true">
    `
    return container
  }

  private buildParameterNamePrefix (index: number): string {
    return `ccm_test[requests_attributes][${this.requestIndex}][request_parameters_attributes][${index}]`
  }

  private getExistingParameterValues (): Record<string, { value: string, id?: string, errors?: string[] }> {
    const values: Record<string, { value: string, id?: string, errors?: string[] }> = {}
    const existingFields = this.parametersFieldsTarget.querySelectorAll(`.${this.PARAMETER_FIELD_CLASS}`)

    existingFields.forEach((field) => {
      const paramName = (field as HTMLElement).dataset.parameterName
      if ((paramName == null) || (paramName === '')) return

      const idInput = field.querySelector('input[name*="[id]"]') as HTMLInputElement
      const checkedRadio = field.querySelector<HTMLInputElement>(`.${this.PARAMETER_VALUE_INPUT_CLASS}[type="radio"]:checked`)
      const textInput = field.querySelector<HTMLInputElement>(`.${this.PARAMETER_VALUE_INPUT_CLASS}:not([type="radio"])`)

      values[paramName] = {
        value: checkedRadio?.value ?? textInput?.value ?? '',
        id: idInput?.value
      }
    })

    return values
  }

  private createParameterField (param: ParameterType, index: number, existingValue?: string, existingId?: string, errors?: string[]): HTMLElement {
    const template = this.parameterTemplateTarget.content.cloneNode(true) as DocumentFragment
    const fieldDiv = template.querySelector(`.${this.PARAMETER_FIELD_CLASS}`) as HTMLElement

    fieldDiv.dataset.parameterName = param.name

    this.setFieldLabel(fieldDiv, param)
    this.setFieldDescription(fieldDiv, param)
    this.setFieldInputs(fieldDiv, param, index, existingValue, existingId)
    this.setFieldErrors(fieldDiv, errors)
    this.attachClearButton(fieldDiv, param.type)

    return fieldDiv
  }

  private setFieldLabel (fieldDiv: HTMLElement, param: ParameterType): void {
    const label = fieldDiv.querySelector('.parameter-label') as HTMLElement
    label.textContent = param.name

    if (param.required) {
      const requiredSpan = fieldDiv.querySelector('.parameter-required') as HTMLElement
      requiredSpan.style.display = 'inline'
    }
  }

  private setFieldDescription (fieldDiv: HTMLElement, param: ParameterType): void {
    const desc = fieldDiv.querySelector('.parameter-description') as HTMLElement
    const description = param.in === 'path'
      ? `Will replace {${param.name}} in the endpoint URL`
      : param.description

    if ((description != null) && (description !== '')) {
      desc.textContent = description
      desc.style.display = 'block'
    }
  }

  private setFieldInputs (fieldDiv: HTMLElement, param: ParameterType, index: number, existingValue?: string, existingId?: string): void {
    const namePrefix = this.buildParameterNamePrefix(index)

    this.setHiddenField(fieldDiv, '.parameter-name-input', `${namePrefix}[name]`, param.name)
    this.setHiddenField(fieldDiv, '.parameter-destroy-input', `${namePrefix}[_destroy]`, '')

    if ((existingId != null) && (existingId !== '')) {
      this.addIdField(fieldDiv, namePrefix, existingId)
    }

    const inputContainer = fieldDiv.querySelector('.parameter-input-container') as HTMLElement
    const input = this.createInputByType(param, namePrefix, existingValue)
    inputContainer.appendChild(input)
  }

  private setHiddenField (container: HTMLElement, selector: string, name: string, value: string): void {
    const input = container.querySelector(selector) as HTMLInputElement
    input.name = name
    if (value !== '') input.value = value
  }

  private addIdField (fieldDiv: HTMLElement, namePrefix: string, existingId: string): void {
    const idInput = document.createElement('input')
    idInput.type = 'hidden'
    idInput.name = `${namePrefix}[id]`
    idInput.value = existingId
    fieldDiv.insertBefore(idInput, fieldDiv.firstChild)
  }

  private setFieldErrors (fieldDiv: HTMLElement, errors?: string[]): void {
    const errorElement = fieldDiv.querySelector('.parameter-error') as HTMLElement
    if ((errors != null) && errors.length > 0) {
      errorElement.textContent = errors.join(', ')
      errorElement.style.display = 'block'

      const input = fieldDiv.querySelector(`.${this.PARAMETER_VALUE_INPUT_CLASS}`) as HTMLInputElement
      if (input != null) {
        input.classList.add('ring-red-500', 'focus:ring-red-500')
      }
    }
  }

  private attachClearButton (fieldDiv: HTMLElement, paramType: string): void {
    const clearBtn = fieldDiv.querySelector('.parameter-clear-btn') as HTMLButtonElement
    clearBtn?.addEventListener('click', () => {
      if (paramType === 'boolean') {
        this.clearBooleanInput(fieldDiv)
      } else {
        this.clearTextInput(fieldDiv)
      }
    })
  }

  private clearBooleanInput (fieldDiv: HTMLElement): void {
    fieldDiv.querySelectorAll<HTMLInputElement>(`.${this.PARAMETER_VALUE_INPUT_CLASS}[type="radio"]`).forEach(r => { r.checked = false })
  }

  private clearTextInput (fieldDiv: HTMLElement): void {
    const input = fieldDiv.querySelector<HTMLInputElement>(`.${this.PARAMETER_VALUE_INPUT_CLASS}`)
    if (input != null) input.value = ''
  }

  private createInputByType (param: ParameterType, namePrefix: string, existingValue?: string): HTMLElement {
    const inputFragment = this.inputTemplateTarget.content.cloneNode(true) as DocumentFragment
    const inputName = `${namePrefix}[value]`
    const wrapper = document.createElement('div')

    if (param.type === 'boolean') {
      this.setupBooleanInput(inputFragment, inputName, param, existingValue)
    } else {
      this.setupTextOrIntegerInput(inputFragment, inputName, param, existingValue)
    }

    Array.from(inputFragment.childNodes)
      .filter(n => n.nodeType === Node.ELEMENT_NODE)
      .forEach(n => wrapper.appendChild(n))

    return wrapper
  }

  private setupBooleanInput (fragment: DocumentFragment, inputName: string, param: ParameterType, existingValue?: string): void {
    fragment.querySelector(`.${this.PARAMETER_VALUE_INPUT_CLASS}:not([type="radio"])`)?.remove()
    fragment.querySelectorAll<HTMLInputElement>(`.${this.PARAMETER_VALUE_INPUT_CLASS}[type="radio"]`).forEach(radio => {
      radio.name = inputName
      radio.required = param.required
      radio.checked = existingValue === radio.value
    })
  }

  private setupTextOrIntegerInput (fragment: DocumentFragment, inputName: string, param: ParameterType, existingValue?: string): void {
    fragment.querySelector('.flex.items-center.space-x-4')?.remove()
    const input = fragment.querySelector(`.${this.PARAMETER_VALUE_INPUT_CLASS}`) as HTMLInputElement
    input.name = inputName
    input.type = param.type === 'integer' ? 'number' : 'text'
    input.required = param.required

    if (param.type === 'integer') {
      input.step = '1'
    } else {
      if ((param.pattern != null) && (param.pattern !== '')) input.pattern = param.pattern
      input.placeholder = this.capitalizeFirst(param.name) + ' placeholder'
    }

    input.value = existingValue ?? ((param.default != null) ? String(param.default) : '')
  }

  private capitalizeFirst (str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  private extractRequestIndex (): number {
    const nameMatch = this.endpointTarget.name.match(/\[requests_attributes]\[(\d+)]/)
    return (nameMatch != null) ? parseInt(nameMatch[1], 10) : 0
  }

  private loadExistingParametersFromDOM (): void {
    if (!Array.isArray(this.existingParametersValue)) return

    this.existingParametersValue.forEach((param) => {
      if ((param.name !== '') && (param.value != null)) {
        this.existingParametersFromServer[param.name] = {
          value: String(param.value),
          id: ((param.id != null) && (param.id !== '')) ? param.id : undefined,
          errors: param.errors
        }
      }
    })
  }

  private toggleVisibility (element: HTMLElement | undefined, visible: boolean): void {
    if (element == null) return
    element.style.display = visible ? 'block' : 'none'
  }
}
